import React, { useState, useMemo, ReactNode } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { ExternalAppHeaderWrapper } from './ExternalAppHeaderWrapper'
import { useStyles } from './ExternalAppRoute.styles'
import { ArrowLeft } from '@mui/icons-material'
import { Button } from '@mui/material'
import clsx from 'clsx'
import { useFlagValue } from '@labrav/flags'
import { WithErrorBoundary } from '@labrav/common'

type ExternalAppRouteProps = {
  title?: string
  titleIcon?: React.ReactNode
  actions?: React.FC
  component?: React.FC
  hideTitle?: boolean
  external?: boolean
  backButton?: boolean
  children?: ReactNode
  scrollBar?: boolean
}

export const ExternalAppRoute: React.FC<ExternalAppRouteProps> = ({
  title,
  actions: Actions,
  component: Component,
  children,
  titleIcon,
  hideTitle,
  backButton,
  scrollBar = true,
}) => {
  const navigate = useNavigate()
  const [pageYOffset, setPageYOffset] = useState(0)
  const { search } = useLocation()

  const { value: hubspotAppImprovementEnabled } = useFlagValue(
    'hubspotAppImprovement'
  )
  const { classes } = useStyles({
    fullHeight: hubspotAppImprovementEnabled,
    scrollBar,
  })
  const noBack = new URLSearchParams(search).get('noBack')?.toLowerCase()
  const renderBackButton = useMemo(
    () => backButton && noBack !== 'true',
    [backButton, noBack]
  )
  const onScrollValue = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.target as HTMLDivElement
    setPageYOffset(target.scrollTop)
  }
  return (
    <>
      <div>
        {renderBackButton && (
          <div
            className={classes.backButtonContainer}
            onClick={() => navigate(-1)}
          >
            <Button size="small" data-testid="back-button">
              <ArrowLeft sx={{ fontSize: '24px' }} /> Back
            </Button>
          </div>
        )}

        {!hubspotAppImprovementEnabled && (
          <div className={classes.stickyExternalHeaders}>
            <ExternalAppHeaderWrapper
              title={title}
              titleIcon={titleIcon}
              actions={Actions}
              hideTitle={hideTitle}
              pageYOffset={pageYOffset}
            />
          </div>
        )}
      </div>
      <div className={classes.root}>
        <div
          className={clsx(classes.content, {
            [classes.withBackButton]: backButton,
          })}
          onScroll={onScrollValue}
        >
          <div data-testid="app-body" className={classes.appBody}>
            <WithErrorBoundary boundary="ExternalAppRoute">
              {Component ? <Component /> : children}
            </WithErrorBoundary>
          </div>
        </div>
      </div>
    </>
  )
}
